import { useEffect, useState } from 'react'
import { useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'

import { Button } from '../../../../../components/Button'
import { DialogModal } from '../../../../../components/DialogModal'
import { SearchInput } from '../../../../../components/SearchInput'
import { Medias } from '../../../../../enumns/medias'
import { useMediaQuery } from '../../../../../hooks/useMediaQuery'
import { useAssetCreateMutation } from '../../../../../services/hooks/assets/mutations/useAssetCreateMutation'
import { useAssetEditMutation } from '../../../../../services/hooks/assets/mutations/useAssetEditMutation'
import { useAssetRemoveMutation } from '../../../../../services/hooks/assets/mutations/useAssetRemoveMutation'
import { type AssetDomain } from '../../../../../types/domain/asset-domain'
import { type AssetPersistence } from '../../../../../types/persistence/asset-persistence'
import { NavigationSelect } from '../../components/NavigationSelect'
import { TableRowActions } from '../../components/TableRowActions'
import { AssetForm } from './components/AssetForm'
import { TableWithManuallyPagination } from './components/TableWithManuallyPagination'
import { TableWithInfiniteScroll } from './components/TableWithInfiniteScroll'
import {
  TOAST_ERROR_DEFAULT_OPTIONS,
  TOAST_SUCCESS_DEFAULT_OPTIONS,
} from '../../../../../constants/toast'
import { getErrorMessage } from '../../../../../utils/getErrorMessage'

export const Asset = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isMinScreenWidthLG = useMediaQuery(`(min-width: ${Medias.LG}px)`)
  const isMinScreenWidthMD = useMediaQuery(`(min-width: ${Medias.MD}px)`)

  const [formModalIsOpen, setFormModalIsOpen] = useState(false)
  const [assetToEdit, setAssetToEdit] = useState()

  const assetRemotionMutation = useAssetRemoveMutation()
  const assetCreationMutation = useAssetCreateMutation()
  const assetEditionMutation = useAssetEditMutation()

  const [filters, setFilters] = useState({
    page: 1,
    take: isMinScreenWidthMD ? 10 : 20,
    search: '',
  })

  const handleSearchAsset = (searchTerm: string) => {
    setFilters((prevState) => ({
      ...prevState,
      page: 1,
      search: searchTerm,
    }))
  }

  const handleRemoveAsset = (id: number, successAction: () => void) => {
    assetRemotionMutation.mutate(id, {
      onSuccess: () => {
        toast.success(
          'Asset deletado com sucesso',
          TOAST_SUCCESS_DEFAULT_OPTIONS
        )

        queryClient.invalidateQueries(['assets', filters])
        successAction()
      },
      onError: (error) => {
        toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS)
      },
    })
  }

  const createAsset = (data: AssetPersistence) => {
    assetCreationMutation.mutate(data, {
      onSuccess () {
        toast.success(
          'Asset cadastrado com sucesso',
          TOAST_SUCCESS_DEFAULT_OPTIONS
        )

        setFormModalIsOpen(false)
        queryClient.invalidateQueries(['assets', filters])
      },
      onError (error) {
        toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS)
      },
    })
  }

  const updateAsset = (id: number, data: AssetPersistence) => {
    assetEditionMutation.mutate(
      { id, data },
      {
        onSuccess () {
          toast.success(
            'Asset editado com sucesso',
            TOAST_SUCCESS_DEFAULT_OPTIONS
          )

          setAssetToEdit(undefined)
          setFormModalIsOpen(false)
          queryClient.invalidateQueries(['assets', filters])
        },
        onError (error) {
          toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS)
        },
      }
    )
  }

  const handleCreateButtonClick = () => {
    if (assetToEdit) {
      setAssetToEdit(undefined)
    }

    setFormModalIsOpen(true)
  }

  const handleSaveAsset = (data: AssetPersistence) => {
    if (assetToEdit) {
      return updateAsset((assetToEdit as any).id, data)
    }

    createAsset(data)
  }

  const getAssetFromCache = (id: number) => {
    const assetsFromCache: any = queryClient.getQueryData(['assets', filters])

    if (isMinScreenWidthMD) {
      return assetsFromCache.data.find(
        (client: AssetDomain) => client.id === id
      )
    }

    return assetsFromCache?.pages
      ?.reduce((acc: any, item: { data: AssetDomain[] }) => {
        return [...acc, ...item.data]
      }, [])
      .find((client: AssetDomain) => client.id === id)
  }

  const columns = [
    {
      field: 'id',
      headerName: 'id',
      isVisible: false,
    },
    {
      field: 'fullName',
      headerName: 'Asset',
      widthInPercentage: 90,
    },
    {
      field: 'actions',
      headerName: 'Ações',
      widthInPercentage: 10,
      renderCell ({ id }: { id: number }) {
        return (
          <TableRowActions
            tableContentType="Asset"
            isRemovingRegister={assetRemotionMutation.isLoading}
            alertMessage="Você está prestes a excluir um Asset. Deseja continuar?"
            onView={() => navigate(`${id}/analysts`)}
            onEdit={() => {
              const assetToEditFromCache = getAssetFromCache(id)
              setAssetToEdit(assetToEditFromCache)
              setFormModalIsOpen(true)
            }}
            onRemove={(successAction) => handleRemoveAsset(id, successAction)}
          />
        )
      },
    },
  ]

  useEffect(() => {
    if (isMinScreenWidthMD) {
      setFilters((prevState) => ({
        ...prevState,
        page: 1,
      }))
      return
    }

    if (!isMinScreenWidthMD) {
      setFilters((prevState) => ({
        ...prevState,
        take: 20,
        page: 1,
      }))
    }
  }, [isMinScreenWidthMD])

  return (
    <>
      <div>
        {!isMinScreenWidthLG && <NavigationSelect defaultRoute="asset" />}

        <div className="md:flex md:justify-between md:items-center md:mb-6">
          <h1 className="mb-4 font-medium text-grey-900 md:text-lg md:mb-0">
            Assets
          </h1>

          <div className="mb-4 md:flex md:gap-2">
            <SearchInput
              onSearch={handleSearchAsset}
              placeholder="Buscar por nome"
              className="mb-6 md:w-96 md:mb-0"
            />

            <Button className="h-10 ml-auto" onClick={handleCreateButtonClick}>
              Cadastrar
            </Button>
          </div>
        </div>

        {isMinScreenWidthMD ? (
          <TableWithManuallyPagination
            columns={columns}
            filters={filters}
            setFilters={setFilters}
          />
        ) : (
          <TableWithInfiniteScroll columns={columns} filters={filters} />
        )}
      </div>

      <DialogModal
        isOpen={formModalIsOpen}
        title={`${assetToEdit ? 'Editar' : 'Cadastrar'} asset`}
        onClose={() => setFormModalIsOpen(false)}
      >
        <AssetForm
          isSubmitting={
            assetCreationMutation.isLoading || assetEditionMutation.isLoading
          }
          defaultValues={assetToEdit}
          onSubmit={handleSaveAsset}
        />
      </DialogModal>
    </>
  )
}
